import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import {
  DerinOrganization,
  DerinOrganizationMember,
  DerinUserRole,
  getOrganizationMembers,
  updateOrganizationMemberRole,
  removeOrganizationMember,
  getSurveysByOrganization,
  Survey,
} from "../../api/derin";
import { useToast } from "./Toast";
import CollectionsTab from "./CollectionsTab";
import SurveyRequestsTab from "./SurveyRequestsTab";
import ModalShell from "./CollectionsTab/ModalShell";
import AddUserModal from "./Users/AddUserModal";
import { ResendInviteMenuItem, useResendInvite } from "./Users/ResendInvite";
import { ROLE_OPTIONS, roleChipSx, selectSx } from "./Users/styles";
import { errorMessageOf } from "./errorMessage";

interface OrganizationDetailViewProps {
  organization: DerinOrganization;
  onBack: () => void;
  onEdit: () => void;
}


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  /**
   * Keeps the panel mounted while another tab is selected, hiding it with CSS instead of
   * tearing it down. A tab that fetches its own data needs this: unmounting throws that
   * data away, so the tab loads from scratch on every visit and never loads at all until
   * the first one.
   */
  keepMounted?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, keepMounted, ...other } = props;
  const isActive = value === index;
  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      style={{ width: "100%", display: isActive ? undefined : "none" }}
      {...other}
    >
      {(isActive || keepMounted) && <Box sx={{ width: "100%" }}>{children}</Box>}
    </div>
  );
}

const OrganizationDetailView: React.FC<OrganizationDetailViewProps> = ({
  organization,
  onBack,
  onEdit,
}) => {
  const { showSuccess, showError } = useToast();
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Members of this organization, each with their role here.
  const [users, setUsers] = useState<DerinOrganizationMember[]>([]);
  // Null count until the first load, so the tab never reads "Users (0)" while loading.
  const [usersLoaded, setUsersLoaded] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await getOrganizationMembers(organization.id);
      setUsers(data);
    } catch (err) {
      console.error("Error fetching users:", err);
      showError("Error fetching users");
    } finally {
      setUsersLoaded(true);
    }
  }, [organization.id, showError]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const organizationUsers = users;

  // Collections state — the tab owns the data; only the count is lifted for the tab label.
  const [collectionsCount, setCollectionsCount] = useState<number | null>(null);
  // Same for survey requests.
  const [surveyRequestsCount, setSurveyRequestsCount] = useState<number | null>(null);

  // Surveys state
  const [surveys, setSurveys] = useState<Survey[]>([]);


  const fetchSurveys = useCallback(async () => {
    try {
      const data = await getSurveysByOrganization(organization.id);
      setSurveys(data);
    } catch (err) {
      console.error("Error fetching surveys:", err);
      showError("Error fetching surveys");
    }
  }, [organization.id, showError]);

  // Fetch surveys when organization changes
  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  // Add / change role / remove state
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [editingUser, setEditingUser] = useState<DerinOrganizationMember | null>(null);
  const [editingRole, setEditingRole] = useState<DerinUserRole>("Viewer");
  const [deleteUserTarget, setDeleteUserTarget] = useState<DerinOrganizationMember | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const { resend: resendInvite, sendingId: resendingInviteId } = useResendInvite();

  // Survey accordion state

  // Create Survey Modal state

  // Upload New Version Modal state

  // Drag and drop states

  // Permissions state - maps userId to surveyId access.
  // Starts empty: the seeded sample users this used to carry were invented, and the
  // Permissions tab that reads it is not reachable yet. It needs a real API before it ships.
  const [permissions, setPermissions] = useState<Record<string, Record<string, boolean>>>({});

  const handleTogglePermission = (userId: string, surveyId: string) => {
    setPermissions(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [surveyId]: !prev[userId]?.[surveyId]
      }
    }));
  };


  // Create Survey Modal handlers



  // Upload New Version Modal handlers



  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  const handleEditUser = () => {
    const user = users.find(u => u.id === selectedUserId);
    if (user) {
      setEditingUser(user);
      setEditingRole(user.role);
    }
    handleMenuClose();
  };

  const handleDeleteUser = () => {
    const user = users.find(u => u.id === selectedUserId);
    if (user) {
      setDeleteUserTarget(user);
    }
    handleMenuClose();
  };

  const handleResendInvite = () => {
    const user = users.find(u => u.id === selectedUserId);
    if (user) resendInvite(user);
    handleMenuClose();
  };

  const handleOpenAddUser = () => {
    setOpenAddUserModal(true);
  };

  const handleSaveRole = async () => {
    if (!editingUser) return;
    try {
      setIsSavingUser(true);
      await updateOrganizationMemberRole(organization.id, editingUser.id, editingRole);
      showSuccess(`${editingUser.firstName} ${editingUser.lastName} is now ${editingRole}`);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      showError(errorMessageOf(err));
    } finally {
      setIsSavingUser(false);
    }
  };

  /** Removes the membership only: the account and its other organizations stay. */
  const handleConfirmDeleteUser = async () => {
    if (!deleteUserTarget) return;
    try {
      setIsDeletingUser(true);
      await removeOrganizationMember(organization.id, deleteUserTarget.id);
      showSuccess(`${deleteUserTarget.firstName} ${deleteUserTarget.lastName} was removed from ${organization.name}`);
      setDeleteUserTarget(null);
      fetchUsers();
    } catch (err) {
      showError(errorMessageOf(err));
    } finally {
      setIsDeletingUser(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "24px 30px",
        gap: "24px",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0px",
          width: "100%",
          minHeight: "52px",
        }}
      >
        {/* Left side - Back button + Org info */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {/* Back Button */}
          <Button
            startIcon={<ArrowBackIcon sx={{ width: 16, height: 16 }} />}
            onClick={onBack}
            sx={{
              height: "32px",
              borderRadius: "8px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#0A0A0A",
              textTransform: "none",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Back to organizations
          </Button>

          {/* Org Icon */}
          <EmojiTransportationIcon sx={{ width: 24, height: 24, color: "#717182" }} />

          {/* Org Info */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "27px",
                letterSpacing: "-0.439453px",
                color: "#0A0A0A",
              }}
            >
              {organization.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "-0.150391px",
                color: "#717182",
              }}
            >
              Created {new Date(organization.dateCreated).toLocaleDateString()}
            </Typography>
          </Box>

          {/* Badge */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: "2px 8px",
              gap: "4px",
              background: organization.isActive ? "#030213" : "#DCDCDC",
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "12px",
                lineHeight: "16px",
                color: organization.isActive ? "#FFFFFF" : "#030213",
              }}
            >
              {organization.isActive ? "Active" : "Inactive"}
            </Typography>
          </Box>
        </Box>

        {/* Right side - Edit Button */}
        <Button
          startIcon={<EditNoteOutlinedIcon sx={{ width: 16, height: 16 }} />}
          onClick={onEdit}
          sx={{
            boxSizing: "border-box",
            height: "32px",
            background: "#FFFFFF",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "20px",
            letterSpacing: "-0.150391px",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": {
              background: "#F3F3F5",
            },
          }}
        >
          Edit organization
        </Button>
      </Box>

      {/* Tabs and Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "32px",
          width: "100%",
        }}
      >
        {/* Tab List */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            width: "100%",
            background: "#ECECF0",
            borderRadius: "14px",
            minHeight: "36px",
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .MuiTabs-flexContainer": {
              height: "36px",
              padding: "3px",
            },
          }}
        >
          <Tab
            label={usersLoaded ? `Users (${organizationUsers.length})` : "Users"}
            sx={{
              flex: 1,
              maxWidth: "none",
              minHeight: "29px",
              height: "29px",
              borderRadius: "14px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#0A0A0A",
              textTransform: "none",
              "&.Mui-selected": {
                background: "#FFFFFF",
                color: "#0A0A0A",
              },
            }}
          />
          <Tab
            label={collectionsCount === null ? "Collections" : `Collections (${collectionsCount})`}
            sx={{
              flex: 1,
              maxWidth: "none",
              minHeight: "29px",
              height: "29px",
              borderRadius: "14px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#0A0A0A",
              textTransform: "none",
              "&.Mui-selected": {
                background: "#FFFFFF",
                color: "#0A0A0A",
              },
            }}
          />
          <Tab
            label={
              surveyRequestsCount === null
                ? "Survey Requests"
                : `Survey Requests (${surveyRequestsCount})`
            }
            sx={{
              flex: 1,
              maxWidth: "none",
              minHeight: "29px",
              height: "29px",
              borderRadius: "14px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#0A0A0A",
              textTransform: "none",
              "&.Mui-selected": {
                background: "#FFFFFF",
                color: "#0A0A0A",
              },
            }}
          />
        </Tabs>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {/* Users Tab - Card */}
          <Box
            sx={{
              boxSizing: "border-box",
              width: "100%",
              background: "#FFFFFF",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: "14px",
            }}
          >
            {/* Card Header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 24px",
              }}
            >
              {/* Title and Description */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "18px",
                    lineHeight: "16px",
                    letterSpacing: "-0.3125px",
                    color: "#0A0A0A",
                  }}
                >
                  User Management
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "24px",
                    letterSpacing: "-0.3125px",
                    color: "#717182",
                    mt: 0.5,
                  }}
                >
                  Manage users and their access permissions
                </Typography>
              </Box>

              {/* Add User Button */}
              <Button
                startIcon={<PersonAddAltOutlinedIcon sx={{ width: 16, height: 16 }} />}
                onClick={handleOpenAddUser}
                sx={{
                  height: "32px",
                  padding: "6px 16px 6px 10px",
                  gap: "8px",
                  background: "#002824",
                  borderRadius: "8px",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "-0.150391px",
                  color: "#FFFFFF",
                  textTransform: "none",
                  "&:hover": {
                    background: "#003d38",
                  },
                }}
              >
                Add User
              </Button>
            </Box>

            {/* Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "-0.150391px",
                        color: "#0A0A0A",
                        border: "none",
                        paddingLeft: "32px",
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "-0.150391px",
                        color: "#0A0A0A",
                        border: "none",
                      }}
                    >
                      Email
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "-0.150391px",
                        color: "#0A0A0A",
                        border: "none",
                      }}
                    >
                      Role
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "-0.150391px",
                        color: "#0A0A0A",
                        border: "none",
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell sx={{ border: "none", width: "52px" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!usersLoaded && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ border: "none", py: 5 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  )}
                  {usersLoaded && organizationUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ border: "none", py: 4, color: "#717182" }}>
                        No users in this organization yet
                      </TableCell>
                    </TableRow>
                  )}
                  {organizationUsers.map((user, index) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        background: index % 2 === 0 ? "rgba(0, 0, 0, 0.05)" : "transparent",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "20px",
                          letterSpacing: "-0.150391px",
                          color: "#0A0A0A",
                          border: "none",
                          paddingLeft: "32px",
                        }}
                      >
                        {user.firstName} {user.lastName}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "20px",
                          letterSpacing: "-0.150391px",
                          color: "#0A0A0A",
                          border: "none",
                        }}
                      >
                        {user.email}
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>
                        <Box component="span" sx={roleChipSx(user.role)}>
                          {user.role}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "2px 8px",
                            background: user.isEmailVerified ? "#030213" : "#DCDCDC",
                            borderRadius: "8px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 500,
                              fontSize: "12px",
                              lineHeight: "16px",
                              color: user.isEmailVerified ? "#FFFFFF" : "#030213",
                            }}
                          >
                            {/* Pending until the invite is accepted and a password set. */}
                            {user.isEmailVerified ? "Active" : "Pending"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: "none", width: "52px" }}>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, user.id)}
                          sx={{
                            width: "36px",
                            height: "32px",
                            borderRadius: "8px",
                          }}
                        >
                          <MoreVertIcon sx={{ color: "#0A0A0A" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1} keepMounted>
          {/* Mounted with the organization, not with the tab: the listing is fetched up
              front so the tab is already populated on the first visit, and it survives
              switching away and back. It reloads on a mutation, or when the organization
              itself changes (remounting this view). */}
          <CollectionsTab organization={organization} onCountChange={setCollectionsCount} />
        </TabPanel>

        {/* Index 3 is the Permissions panel, which has no tab. */}
        <TabPanel value={tabValue} index={2} keepMounted>
          <SurveyRequestsTab organization={organization} onCountChange={setSurveyRequestsCount} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Under Construction Banner */}
          <Box
            sx={{
              boxSizing: "border-box",
              width: "100%",
              background: "#FEF3C7",
              border: "1px solid #F59E0B",
              borderRadius: "10px",
              padding: "12px 16px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#92400E",
              }}
            >
              This tab is still under construction. The data shown here is for UI testing purposes only.
            </Typography>
          </Box>

          {/* Permissions Tab - Card */}
          <Box
            sx={{
              boxSizing: "border-box",
              width: "100%",
              background: "#FFFFFF",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: "14px",
              padding: "24px 0px 24px 24px",
              display: "flex",
              flexDirection: "column",
              gap: "40px",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <ShieldOutlinedIcon sx={{ width: 20, height: 20, color: "#0A0A0A" }} />
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "18px",
                    lineHeight: "16px",
                    letterSpacing: "-0.3125px",
                    color: "#0A0A0A",
                  }}
                >
                  Survey Permissions
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "24px",
                  letterSpacing: "-0.3125px",
                  color: "#717182",
                  marginTop: "8px",
                }}
              >
                Control which users can access each survey
              </Typography>
            </Box>

            {/* Permissions Table */}
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow
                    sx={{
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                      height: "43px",
                    }}
                  >
                    <TableCell
                      sx={{
                        width: "250px",
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "-0.150391px",
                        color: "#0A0A0A",
                        border: "none",
                        padding: "8px",
                      }}
                    >
                      User
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "100px",
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "-0.150391px",
                        color: "#0A0A0A",
                        border: "none",
                        padding: "8px",
                      }}
                    >
                      Role
                    </TableCell>
                    {/* Dynamic Survey Columns */}
                    {surveys.map((survey) => (
                      <TableCell
                        key={survey.originalId}
                        sx={{
                          minWidth: "200px",
                          border: "none",
                          padding: "8px",
                          textAlign: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 500,
                              fontSize: "12px",
                              lineHeight: "16px",
                              textAlign: "center",
                              color: "#0A0A0A",
                            }}
                          >
                            {survey.title}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              padding: "2px 8px",
                              border: "1px solid rgba(0, 0, 0, 0.1)",
                              borderRadius: "8px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 500,
                                fontSize: "12px",
                                lineHeight: "16px",
                                textAlign: "center",
                                color: "#0A0A0A",
                              }}
                            >
                              v{survey.version}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organizationUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                        height: "53px",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      {/* User Cell */}
                      <TableCell
                        sx={{
                          border: "none",
                          padding: "8px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 500,
                              fontSize: "14px",
                              lineHeight: "20px",
                              letterSpacing: "-0.150391px",
                              color: "#0A0A0A",
                            }}
                          >
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 400,
                              fontSize: "12px",
                              lineHeight: "16px",
                              color: "#717182",
                            }}
                          >
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      {/* Role Cell */}
                      <TableCell
                        sx={{
                          border: "none",
                          padding: "8px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "2px 8px",
                            background: "#DCDCDC",
                            borderRadius: "8px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 500,
                              fontSize: "12px",
                              lineHeight: "16px",
                              color: "#030213",
                            }}
                          >
                            User
                          </Typography>
                        </Box>
                      </TableCell>
                      {/* Survey Permission Checkboxes */}
                      {surveys.map((survey) => (
                        <TableCell
                          key={survey.originalId}
                          sx={{
                            border: "none",
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          <Box
                            onClick={() => handleTogglePermission(user.id, survey.originalId)}
                            sx={{
                              display: "inline-flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "16px",
                              height: "16px",
                              background: permissions[user.id]?.[survey.originalId] ? "#030213" : "#FFFFFF",
                              border: permissions[user.id]?.[survey.originalId] ? "1px solid #030213" : "1px solid rgba(0, 0, 0, 0.1)",
                              borderRadius: "4px",
                              cursor: "pointer",
                              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                            }}
                          >
                            {permissions[user.id]?.[survey.originalId] && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                <path
                                  d="M1 4L3.5 6.5L9 1"
                                  stroke="#FFFFFF"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
          },
        }}
      >
        <MenuItem
          onClick={handleEditUser}
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: "14px",
          }}
        >
          Change role
        </MenuItem>
        <ResendInviteMenuItem
          user={users.find(u => u.id === selectedUserId)}
          sendingId={resendingInviteId}
          onClick={handleResendInvite}
          sx={{ fontFamily: "Inter, sans-serif", fontSize: "14px" }}
        />
        <MenuItem
          onClick={handleDeleteUser}
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: "14px",
            color: "#DC2626",
          }}
        >
          Remove from organization
        </MenuItem>
      </Menu>

      {openAddUserModal && (
        <AddUserModal
          open
          organizations={[organization]}
          scopedOrganization={organization}
          onClose={() => setOpenAddUserModal(false)}
          onSaved={fetchUsers}
        />
      )}

      <ModalShell
        open={!!editingUser}
        onClose={() => setEditingUser(null)}
        title="Change role"
        subtitle={
          editingUser
            ? `${editingUser.firstName} ${editingUser.lastName}'s role in ${organization.name}. Their access to other organizations is not affected.`
            : undefined
        }
        confirmLabel="Save"
        onConfirm={handleSaveRole}
        confirmDisabled={!editingUser || editingRole === editingUser.role}
        isSubmitting={isSavingUser}
      >
        <Box
          component="select"
          aria-label="Role"
          value={editingRole}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEditingRole(e.target.value as DerinUserRole)}
          sx={selectSx}
        >
          {ROLE_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </Box>
      </ModalShell>

      <ModalShell
        open={!!deleteUserTarget}
        onClose={() => setDeleteUserTarget(null)}
        title="Remove from organization?"
        subtitle={
          deleteUserTarget
            ? `${deleteUserTarget.firstName} ${deleteUserTarget.lastName} will lose access to ${organization.name}. Their account and access to other organizations stay.`
            : undefined
        }
        confirmLabel="Remove"
        onConfirm={handleConfirmDeleteUser}
        isSubmitting={isDeletingUser}
        danger
      />

      {/* Create Survey Modal */}

      {/* Upload New Version Modal */}
    </Box>
  );
};

export default OrganizationDetailView;
